/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { doc, getDoc } from "firebase/firestore"
import { db } from "../../firebase"
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import PrintIcon from '@mui/icons-material/Print'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import dayjs from "dayjs"

type DocumentType = 'quotation' | 'delivery'

const ISSUER = {
  name: 'りんどう',
  address: '北海道歌志内市字歌神36',
  phone: '0125-35-9107',
}

// 税率が未設定の商品（部位マスタ登録前の旧データ等）は8%（軽減税率）とみなす
const DEFAULT_TAX_RATE = 8

const ShipmentDocument = () => {
  const { shipmentId, type } = useParams<{ shipmentId: string; type: DocumentType }>()
  const navigate = useNavigate()
  const [shipment, setShipment] = useState<Record<string, any> | null>(null)
  const [notFound, setNotFound] = useState<boolean>(false)
  const [memo, setMemo] = useState<string>('')
  const [recipient, setRecipient] = useState<string>('')
  const [honorific, setHonorific] = useState<'御中' | '様'>('御中')
  const [shippingFee, setShippingFee] = useState<number>(0)
  const [discount, setDiscount] = useState<number>(0)
  const [qualifiedInvoiceNumber, setQualifiedInvoiceNumber] = useState<string>('')

  const isQuotation = type === 'quotation'
  const title = isQuotation ? '御見積書' : '納品書'
  const docNamePrefix = isQuotation ? '見積書' : '納品書'
  const lead = isQuotation ? '下記の通り、御見積申し上げます。' : '下記の通り、納品いたします。'

  useEffect(() => {
    getDoc(doc(db, 'settings', 'issuer')).then((snap) => {
      if (snap.exists()) {
        const data = snap.data()
        if (typeof data.qualifiedInvoiceNumber === 'string') setQualifiedInvoiceNumber(data.qualifiedInvoiceNumber)
      }
    })
  }, [])

  useEffect(() => {
    if (!shipmentId) return
    getDoc(doc(db, 'shipments', shipmentId)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data()
        setShipment({ id: snap.id, ...data })
        // 既存の出荷登録時メモを備考欄の初期値として読み込む
        if (typeof data.shippingMemo === 'string') setMemo(data.shippingMemo)
        if (typeof data.customer === 'string') setRecipient(data.customer)
        // 出荷情報に登録済みの送料・値引きを初期値として読み込む（帳票発行時に上書き可能）
        if (data.shippingFee != null) setShippingFee(Number(data.shippingFee) || 0)
        if (data.discount != null) setDiscount(Number(data.discount) || 0)
      } else {
        setNotFound(true)
      }
    })
  }, [shipmentId])

  useEffect(() => {
    if (!shipment) return
    const originalTitle = document.title
    const customerName = recipient || shipment.customer || ''
    const datePart = dayjs().format('YYYYMMDD')
    // 印刷（PDF保存）ダイアログの初期ファイル名として利用される
    document.title = `${docNamePrefix}${customerName}_${datePart}.pdf`
    return () => { document.title = originalTitle }
  }, [shipment, recipient, docNamePrefix])

  if (notFound) {
    return (
      <Box m={4}>
        <Typography variant="h5">該当する出荷情報が見つかりません</Typography>
        <Box mt={2}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/shipments')}>
            出荷一覧に戻る
          </Button>
        </Box>
      </Box>
    )
  }

  if (!shipment) {
    return <Box m={4}><Typography>読み込み中...</Typography></Box>
  }

  const products: any[] = Array.isArray(shipment.products) ? shipment.products : []
  const totalPriceIncTax = Number(shipment.totalPrice) || 0

  // 税率ごと（8%・10%混在時はインボイス制度上、税率ごとに区分して記載する必要がある）に小計・消費税を集計
  const amountByRate = new Map<number, number>()
  products.forEach((p) => {
    const rate = p.taxRate === 10 ? 10 : DEFAULT_TAX_RATE
    amountByRate.set(rate, (amountByRate.get(rate) || 0) + (Number(p.price) || 0))
  })
  if (amountByRate.size === 0 && totalPriceIncTax > 0) {
    amountByRate.set(DEFAULT_TAX_RATE, totalPriceIncTax)
  }
  const taxGroups = Array.from(amountByRate.entries())
    .sort(([a], [b]) => a - b)
    .map(([rate, incTax]) => {
      const groupSubtotal = Math.round(incTax / (1 + rate / 100))
      return { rate, incTax, subtotal: groupSubtotal, tax: incTax - groupSubtotal }
    })

  const grandTotal = totalPriceIncTax + (shippingFee || 0) - (discount || 0)
  const issuedDate = dayjs().format('YYYY年MM月DD日')

  return (
    <Box>
      {/* 印刷時にブラウザ既定のヘッダ/フッタ（URL・日付・ページ番号）を非表示にする */}
      <style dangerouslySetInnerHTML={{ __html: `
        @page { size: A4; margin: 15mm; }
        @media print {
          html, body { margin: 0 !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; break-inside: avoid-page; }
          thead { display: table-header-group; }
        }
      ` }} />
      {/* 操作バー（印刷時は非表示） */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          '@media print': { display: 'none' },
        }}
      >
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(`/admin/shipments/${shipmentId}`)}>
          詳細に戻る
        </Button>
        <TextField
          label="宛先"
          size="small"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          sx={{ width: 200 }}
        />
        <ToggleButtonGroup
          value={honorific}
          exclusive
          size="small"
          onChange={(_, v) => { if (v) setHonorific(v) }}
        >
          <ToggleButton value="御中">御中</ToggleButton>
          <ToggleButton value="様">様</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          label="送料（税込）"
          type="number"
          size="small"
          value={shippingFee}
          onChange={(e) => setShippingFee(Math.max(0, parseInt(e.target.value) || 0))}
          inputProps={{ min: 0, step: 100 }}
          sx={{ width: 160 }}
        />
        <TextField
          label="値引き"
          type="number"
          size="small"
          value={discount}
          onChange={(e) => setDiscount(Math.max(0, parseInt(e.target.value) || 0))}
          inputProps={{ min: 0, step: 100 }}
          sx={{ width: 160 }}
        />
        <TextField
          label="備考"
          size="small"
          multiline
          minRows={1}
          maxRows={4}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          sx={{ width: 320 }}
        />
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          印刷 / PDF保存
        </Button>
      </Box>

      {/* 帳票本体 */}
      <Box
        sx={{
          maxWidth: '210mm',
          minHeight: '297mm',
          mx: 'auto',
          p: '20mm',
          bgcolor: 'white',
          color: '#000',
          fontFamily: '"Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif',
          '@media print': {
            maxWidth: 'none',
            minHeight: 'auto',
            margin: 0,
            padding: 0,
            boxShadow: 'none',
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <Box>
            <Typography variant="body2">発行日: {issuedDate}</Typography>
            {!isQuotation && (
              <Typography variant="body2">出荷日: {shipment.shippingDate || '-'}</Typography>
            )}
          </Box>
          <Typography variant="h4" sx={{ borderBottom: '3px double #000', px: 4, py: 1, fontWeight: 'bold' }}>
            {title}
          </Typography>
          <Box sx={{ width: 120 }} />
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={4}>
          <Box>
            <Typography variant="h5" sx={{ borderBottom: '1px solid #000', pb: 0.5, mb: 1, minWidth: '60mm' }}>
              {recipient || ''} {honorific}
            </Typography>
            <Typography variant="body2">{lead}</Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{ISSUER.name}</Typography>
            <Typography variant="body2">{ISSUER.address}</Typography>
            <Typography variant="body2">TEL: {ISSUER.phone}</Typography>
            {qualifiedInvoiceNumber && (
              <Typography variant="body2">登録番号: {qualifiedInvoiceNumber}</Typography>
            )}
          </Box>
        </Box>

        <Box mb={3} display="flex" justifyContent="flex-end">
          <Box sx={{ border: '2px solid #000', px: 3, py: 1.5 }}>
            <Typography variant="body2" textAlign="center">合計金額（税込）</Typography>
            <Typography variant="h4" textAlign="right" sx={{ fontWeight: 'bold' }}>
              ¥{grandTotal.toLocaleString('ja-JP')}
            </Typography>
          </Box>
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small" sx={{ '& td, & th': { color: '#000', borderColor: '#000' } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                <TableCell sx={{ fontWeight: 'bold', width: '40px' }}>No</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>商品番号</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>品名（部位）</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>税率</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>温度</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>重量(g)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>数量</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>金額（税込）</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p, i) => (
                <TableRow key={p.id || i}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{p.productNo || '-'}</TableCell>
                  <TableCell>{p.part || '-'}</TableCell>
                  <TableCell>{p.taxRate === 10 ? 10 : DEFAULT_TAX_RATE}%</TableCell>
                  <TableCell>{p.storageType || '-'}</TableCell>
                  <TableCell align="right">{p.weight ?? '-'}</TableCell>
                  <TableCell align="right">{p.quantity ?? 1}</TableCell>
                  <TableCell align="right">
                    {p.price != null ? `¥${Number(p.price).toLocaleString('ja-JP')}` : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 合計（税率ごとに区分して記載） */}
        <Box display="flex" justifyContent="flex-end" mb={3} sx={{ '@media print': { pageBreakInside: 'avoid', breakInside: 'avoid' } }}>
          <Box sx={{ minWidth: '70mm', border: '1px solid #000' }}>
            {taxGroups.map((g) => (
              <Box key={g.rate} display="flex" justifyContent="space-between" px={2} py={0.5} sx={{ borderBottom: '1px solid #000' }}>
                <Typography variant="body2">
                  小計（税抜・{g.rate}%対象）／消費税（{g.rate}%）
                </Typography>
                <Typography variant="body2">
                  ¥{g.subtotal.toLocaleString('ja-JP')} ／ ¥{g.tax.toLocaleString('ja-JP')}
                </Typography>
              </Box>
            ))}
            <Box display="flex" justifyContent="space-between" px={2} py={0.5} sx={{ borderBottom: '1px solid #000' }}>
              <Typography variant="body2">商品計（税込）</Typography>
              <Typography variant="body2">¥{totalPriceIncTax.toLocaleString('ja-JP')}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" px={2} py={0.5} sx={{ borderBottom: '1px solid #000' }}>
              <Typography variant="body2">送料（税込）</Typography>
              <Typography variant="body2">¥{(shippingFee || 0).toLocaleString('ja-JP')}</Typography>
            </Box>
            {discount > 0 && (
              <Box display="flex" justifyContent="space-between" px={2} py={0.5} sx={{ borderBottom: '1px solid #000' }}>
                <Typography variant="body2">値引き</Typography>
                <Typography variant="body2">-¥{discount.toLocaleString('ja-JP')}</Typography>
              </Box>
            )}
            <Box display="flex" justifyContent="space-between" px={2} py={0.5} sx={{ bgcolor: '#f0f0f0' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>合計（税込）</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>¥{grandTotal.toLocaleString('ja-JP')}</Typography>
            </Box>
          </Box>
        </Box>

        {memo && (
          <Box mb={2} sx={{ '@media print': { pageBreakInside: 'avoid', breakInside: 'avoid' } }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>備考</Typography>
            <Typography variant="body2" sx={{ borderTop: '1px solid #000', pt: 1, whiteSpace: 'pre-wrap' }}>
              {memo}
            </Typography>
          </Box>
        )}

        {!isQuotation && shipment.trackingNumber && (
          <Box mb={2}>
            <Typography variant="body2">
              {shipment.deliveryCarrier ? `${shipment.deliveryCarrier} ` : ''}宅配便番号: {shipment.trackingNumber}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ShipmentDocument
